/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { COMPANY_SEARCH_CLIENT } from "../../../../../utils/consts";
import { useNaicsAncestorDescendantMap } from "../../../../../legacy/hooks/useNaicsAncestorDescendantMap";
import { SEGMENT_ANALYSIS_NAICS_BREAKDOWN_QUERY } from "../../../../../legacy/queries/MI";
import { CompanyFilter } from "../../../../../legacy/types/companyFilter";
import { HgNaics } from "../../../../../types/taxonomy";
import {
  MarketSpendData,
  NAICSBreakdownQueryPayload,
  NAICSBreakdownQueryVariables,
  SpendBreakdownResult,
} from "../../../../../legacy/types/companySearch/MI";
import { getNaicsRootIds } from "../../../../../legacy/utils/spog";
import { useWorkspaceStore } from "../../../../../store/workspace/WorkspaceStore";
import {
  rollUpMarketSpendByIndustry,
  transformToSpendBreakDownData,
} from "../../../../../legacy/utils/MI";

interface Props {
  companyFilters: CompanyFilter;
  customSpendCategory?: {
    name: string;
    categories: Array<{ id: string; weight: number }>;
  };
  localizeInstalls?: boolean;
}

interface SpendByIndustryChartData {
  loading: boolean;
  spendByIndustryChartData: SpendBreakdownResult[];
  descendantMap: Record<string, HgNaics[]>;
}

export function useGetSpendByIndustryData(
  props: Props
): SpendByIndustryChartData {
  const workspaceWhitespace = useWorkspaceStore(
    (state) => state.draft?.configuration?.whitespace
  );

  const {
    ancestorMap,
    descendantMap,
    allNaics,
    loading: naicsLoading,
  } = useNaicsAncestorDescendantMap();

  const allNaicsMap = useMemo(() => {
    const naicsMap = {} as Record<string, HgNaics>;
    for (const naics of allNaics) {
      naicsMap[naics.id] = naics;
    }

    return naicsMap;
  }, [allNaics]);

  const { data, loading } = useQuery<
    NAICSBreakdownQueryPayload,
    NAICSBreakdownQueryVariables
  >(SEGMENT_ANALYSIS_NAICS_BREAKDOWN_QUERY, {
    variables: {
      companyFilters: props.companyFilters,
      customSpendCategory: props.customSpendCategory,
      localizeInstalls: props.localizeInstalls,
      companyMatchFile:
        props.companyFilters?.opportunityType &&
        workspaceWhitespace?.fileId &&
        workspaceWhitespace?.lastMatched
          ? workspaceWhitespace
          : undefined,
    },
    context: {
      clientName: COMPANY_SEARCH_CLIENT,
    },
  });

  const spendByIndustryData = useMemo<Array<MarketSpendData>>(
    () =>
      data?.segmentAnalysisNaicsBreakdown.map(
        ({ companyCount, naicsId, totalSpend }) => ({
          companyCount,
          sumRelativeValues: totalSpend,
          naics_2012: allNaicsMap[naicsId],
        })
      ) ?? ([] as Array<MarketSpendData>),
    [allNaicsMap, data?.segmentAnalysisNaicsBreakdown]
  );

  const aggregatedIndustryData = useMemo(() => {
    if (loading || naicsLoading) {
      return [];
    }
    const [selectedParentNaics] = getNaicsRootIds(
      props.companyFilters.industry || {},
      {
        naics2012: { data: allNaics },
      }
    );

    const result = [];

    switch (selectedParentNaics.length) {
      case 0:
        result.push(
          ...rollUpMarketSpendByIndustry(
            spendByIndustryData,
            allNaics.filter((naics) => naics.parentId == "0"),
            ancestorMap
          )
        );
        break;
      case 1:
        // @ts-ignore
        if (descendantMap[selectedParentNaics[0]]?.length == 1) {
          // leaf node
          result.push(
            ...rollUpMarketSpendByIndustry(
              spendByIndustryData,
              allNaics.filter((naics) => naics.id == selectedParentNaics[0]),
              ancestorMap
            )
          );
        } else {
          result.push(
            ...rollUpMarketSpendByIndustry(
              spendByIndustryData,
              allNaics.filter(
                (naics) => naics.parentId == selectedParentNaics[0]
              ),
              ancestorMap
            )
          );
        }
        break;
      default:
        result.push(
          ...rollUpMarketSpendByIndustry(
            spendByIndustryData,
            allNaics.filter((naics) => selectedParentNaics.includes(naics.id)),
            ancestorMap
          )
        );
    }

    return result;
  }, [
    props.companyFilters.industry,
    spendByIndustryData,
    allNaics,
    ancestorMap,
    descendantMap,
    loading,
    naicsLoading,
  ]);

  const spendByIndustryChartData = useMemo(() => {
    if (loading || naicsLoading) {
      return [];
    }

    return aggregatedIndustryData
      .map(transformToSpendBreakDownData)
      .sort((dataA, dataB) => dataA.spend - dataB.spend);
  }, [loading, naicsLoading, aggregatedIndustryData]);

  if (loading || naicsLoading) {
    return {
      loading: true,
      spendByIndustryChartData: [],
      descendantMap: {},
    };
  }

  return {
    loading: false,
    spendByIndustryChartData,
    descendantMap: descendantMap as Record<string, HgNaics[]>,
  };
}
